import React from "react";
import { Backdrop, Box, Fade, Modal, Typography } from "@mui/material";
import Loader from "./Loader";

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel: () => void;
  hideCancel?: boolean;
  icon?: React.ReactNode;
  // Shows a spinner in place of confirmText and disables both buttons -- every caller of
  // this dialog (Deactivate/Delete Account, Cancel Subscription, etc.) previously had no
  // feedback at all while its onConfirm action was in flight.
  confirmLoading?: boolean;
}

export default function ConfirmDialog({
  open,
  title = "Confirm",
  message = "Are you sure?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  hideCancel = false,
  icon,
  confirmLoading = false,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 200 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            maxWidth: "calc(100vw - 32px)",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {message}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            {!hideCancel && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onCancel}
                disabled={confirmLoading}
              >
                {cancelText}
              </button>
            )}
            {onConfirm && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={onConfirm}
                disabled={confirmLoading}
              >
                {confirmLoading ? <Loader size={16} /> : confirmText}
              </button>
            )}
            {hideCancel && !onConfirm && (
              <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
                {cancelText}
              </button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

